import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { environment } from '../config/environment';
import { apiRequest, validateSession } from '../services/apiClient';
import './LoginPage.css';

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  const renderLogoLoader = () => (
    <div className="mini-logo-loader" role="status" aria-live="polite" aria-label="Loading">
      <span className="loader-orbit loader-orbit-one"></span>
      <span className="loader-orbit loader-orbit-two"></span>
      <span className="loader-orbit loader-orbit-three"></span>
      <img
        src={process.env.PUBLIC_URL + '/images/logo.png'}
        alt="Aryavihar Security Services"
        className="session-loader-logo"
      />
    </div>
  );

  useEffect(() => {
    const checkSession = async () => {
      try {
        const isValid = await validateSession();
        if (isValid) {
          navigate('/welcome', { replace: true });
          return;
        }
      } catch {
        // Keep user on login page
      } finally {
        setIsCheckingSession(false);
      }
    };

    checkSession();
  }, [navigate]);

  const validate = () => {
    const nextErrors = {};

    if (!formData.email.trim()) {
      nextErrors.email = 'Email is required.';
    } else if (!/^(?!.*\.\.)[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.email.trim())) {
      nextErrors.email = 'Enter a valid email address.';
    }

    if (!formData.password.trim()) {
      nextErrors.password = 'Password is required.';
    } else if (formData.password.trim().length < 6) {
      nextErrors.password = 'Password must be at least 6 characters.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const { ok, data } = await apiRequest(
        environment.endpoints.login,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email.trim(),
            password: formData.password,
          }),
        },
        { skipValidation: true }
      );

      setIsSubmitting(false);

      if (!ok) {
        await Swal.fire({
          icon: 'error',
          text: data?.message || 'Login failed. Please check your credentials.',
          confirmButtonColor: '#d44444',
          confirmButtonText: 'Try Again'
        });
        return;
      }

      const isLoginSuccess = data?.response === true;

      if (!isLoginSuccess) {
        await Swal.fire({
          icon: 'error',
          text: data?.message || 'Login failed. Please try again.',
          confirmButtonColor: '#d44444',
          confirmButtonText: 'Try Again'
        });
        return;
      }

      if (data.user) {
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem('authUser', JSON.stringify(data.user));
      }

      localStorage.removeItem('logoutRequested');
      sessionStorage.removeItem('logoutRequested');

       navigate('/welcome', { replace: true });
      await Swal.fire({
        icon: 'success',
        text: data?.message || 'Welcome back!',
        confirmButtonColor: '#2c4a7a',
        confirmButtonText: 'Continue',
        timer: 2000,
        timerProgressBar: true
      });

     
    } catch (error) {
      setIsSubmitting(false);

      await Swal.fire({
        icon: 'error',
        text: error?.message || 'An unexpected error occurred. Please try again.',
        confirmButtonColor: '#d44444',
        confirmButtonText: 'Try Again'
      });
    }
  };

  if (isCheckingSession) {
    return (
      <main className="login-page">
        <section className="login-shell session-loader-shell">
          <div className="session-loader-wrap">{renderLogoLoader()}</div>
        </section>
      </main>
    );
  }

  return (
    <main className="login-page">
      {isSubmitting && (
        <div className="api-loader-overlay">
          {renderLogoLoader()}
        </div>
      )}

      <section className="login-shell">
        {/* Brand Panel */}
        <div className="login-brand-panel">
          <div className="login-brand-top">
            <img
              src={process.env.PUBLIC_URL + '/images/logo.png'}
              alt="Aryavihar Security Services"
              className="login-brand-logo"
            />
          </div>
          <p className="login-kicker">ARYAVIHAR SECURITY SERVICES</p>
          <h1>Welcome Back</h1>
          <p>Securely access your dashboard, service updates, and request history.</p>
        </div>

        {/* Form Panel */}
        <div className="login-form-panel">
          <h2>Sign In</h2>
          <p className="login-subtitle"></p>

          <form onSubmit={handleSubmit} noValidate className="login-form">
            <div className="login-field">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                autoComplete="email"
                disabled={isSubmitting}
              />
              {errors.email && <p className="login-error">{errors.email}</p>}
            </div>

            <div className="login-field">
              <label htmlFor="password">Password</label>
              <div className="password-field-wrap">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  className="password-eye-btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  disabled={isSubmitting}
                >
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
              {errors.password && <p className="login-error">{errors.password}</p>}
            </div>

            <div className="login-row">
              <label className="remember-wrap" htmlFor="rememberMe">
                <input
                  id="rememberMe"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(event) => setRememberMe(event.target.checked)}
                  disabled={isSubmitting}
                />
                Remember me
              </label>
              <a href="#login" className="forgot-link">Forgot password?</a>
            </div>

            <button type="submit" className="login-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Logging in...' : 'Sign In'}
            </button>

            <Link to="/" className="back-login-btn" aria-label="Back to home">
              Back to Home
            </Link>
          </form>
        </div>
      </section>
    </main>
  );
};

export default LoginPage;